import { z } from 'zod';
import { APPLICATION_STATUSES, SCREENING_DECISION_KINDS } from '../enums.js';
import { emailSchema, optionalText, phoneSchema, text, uuidSchema } from './common.js';
export const applicationStatusSchema = z.enum(APPLICATION_STATUSES);
export const screeningDecisionKindSchema = z.enum(SCREENING_DECISION_KINDS);
/**
 * The one unauthenticated account-creation path in the app — see
 * `POST /api/applicant-sign-up` (modules/applications/routes.ts) and the
 * extended comment in `lib/auth.ts`. Mirrors `bootstrap-admin.ts`'s own
 * MIN_PASSWORD_LENGTH; kept in sync deliberately, not re-derived from
 * `lib/auth.ts`'s Better Auth config, since that config is for the sign-IN
 * path this route calls into afterward, not this route's own validation.
 */
export const applicantSignUpSchema = z.object({
    firstName: text(100),
    lastName: text(100),
    email: emailSchema,
    phone: phoneSchema.optional(),
    password: z.string().min(12).max(512),
});
export const createApplicationSchema = z.object({
    unitId: uuidSchema,
});
/**
 * SSN is optional here even though the column pair (cipher + last4) is not
 * nullable-by-omission at the database level — an applicant may save
 * `legalAgeAttested` on one step and come back for the SSN on another, and
 * the service only touches columns actually present in the body.
 */
export const updateApplicantSensitiveSchema = z.object({
    ssn: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'Enter a 9-digit SSN with no dashes.')
        .optional(),
    legalAgeAttested: z.boolean().optional(),
    /** Set true to stamp `authorizationGivenAt` with the server's own clock. */
    authorizationGiven: z.boolean().optional(),
});
export const startApplicationFeeSchema = z.object({});
export const recordScreeningResultSchema = z.object({
    vendorReference: optionalText(255),
    reasonCodes: z.array(text(64)).optional(),
    /** One entry per criterion actually checked — never every published criterion. */
    criteriaResults: z
        .array(z.object({
        criterionId: uuidSchema,
        passed: z.boolean(),
        note: optionalText(500),
    }))
        .optional(),
    notes: optionalText(2000),
});
export const decideApplicationSchema = z
    .object({
    decision: screeningDecisionKindSchema,
    screeningCriterionId: uuidSchema.optional(),
})
    .refine((v) => v.decision !== 'DECLINED' || v.screeningCriterionId !== undefined, {
    message: 'A decline requires a screening criterion.',
    path: ['screeningCriterionId'],
});
/**
 * Insert-only, per invariant 8: editing a published criterion creates a new
 * version rather than mutating the one applications may already point at.
 */
export const createScreeningCriterionSchema = z.object({
    code: text(80),
    description: text(4000),
    isPublished: z.boolean().default(true),
});
//# sourceMappingURL=applications.js.map